import React, { useEffect } from 'react';
import styled, { css } from 'styled-components';
import { Images } from '../../../@resources/img';
import { telemetry } from '../../../@vendor/Posthog';
import { UniversalSurfaceRenderer } from '../../../platform/UniversalSurfaceRenderer';
import { Flexer } from '../../dashboard/elements/LayoutElements';

type SnakeProps = {
    width?: number,
    height?: number,
    onScore(score: number): void,
    onGameOver(): void,
};

const Snake = (props: SnakeProps) => {

    const [canvas, setCanvas] = React.useState<HTMLCanvasElement | null>(null);

    useEffect(() => {
        if (!canvas) return;

        // TODO: fix button queueing bug -> need a delay?
        const SNAKE_SIZE = 20;
        const SNAKE_COLOR = 'dimgray';

        const foodAsset = new Image();
        foodAsset.src = Images.Apple;

        const gfx = UniversalSurfaceRenderer.from(canvas);

        let ticksPerSecond = 10;
        let lastTick = 0;
        let score = 0;
        let dx = 0;
        let dy = 0;
        let foodX; let foodY;

        const snake = [{
            x: canvas.width / 2,
            y: canvas.height / 2
        }];

        function drawSnake() {
            snake.forEach(sg => {
                gfx.fillStyle = SNAKE_COLOR;
                gfx.fillRoundRect(sg.x, sg.y, SNAKE_SIZE, SNAKE_SIZE, 5);
            });
        }

        function updateSnake() {
            let x; let y;
            if (snake[0].x + dx >= canvas.width) {
                x = 0;
            }
            else if (snake[0].x + dx <= -SNAKE_SIZE) {
                x = canvas.width - SNAKE_SIZE;
            }
            else {
                x = snake[0].x + dx;
            }
            if (snake[0].y + dy >= canvas.height) {
                y = 0;
            }
            else if (snake[0].y + dy <= -SNAKE_SIZE) {
                y = canvas.height - SNAKE_SIZE;
            }
            else {
                y = snake[0].y + dy;
            }
            const head = {
                x,
                y
            };
            snake.unshift(head);
            if (head.x === foodX && head.y === foodY) {
                [foodX, foodY] = randomCoordinate();
                score++;
                props.onScore(score);
                ticksPerSecond += 0.5;
            }
            else {
                snake.pop();
            }
        }

        function drawFood() {
            if (!foodX && !foodY) {
                [foodX, foodY] = randomCoordinate();
            }
            gfx.drawImage(foodAsset, foodX, foodY, SNAKE_SIZE, SNAKE_SIZE);
        }

        function isGameOver() {
            return !!snake.slice(1).find(sg => snake[0].x === sg.x && snake[0].y === sg.y);
        }

        const isMounted = () => {
            // Check canvas is child of document
            let p = canvas.parentElement;
            while (p) {
                if (p === document.body) {
                    return true;
                }
                p = p.parentElement;
            }
            return false;
        };

        function gameTick() {
            const now = Date.now();

            if (now - lastTick < 1000 / ticksPerSecond) {
                requestAnimationFrame(gameTick);
            }
            else {
                clearCanvas();
                updateSnake();
                drawFood();
                drawSnake();

                if (isGameOver()) {
                    props.onGameOver();
                }
                else if (isMounted()) {
                    requestAnimationFrame(gameTick);
                }

                lastTick = now;
            }
        }

        function clearCanvas() {
            gfx.clearRect(0, 0, canvas.width, canvas.height);
        }

        function randomCoordinate() {
            return [Math.floor(Math.random() * canvas.width / SNAKE_SIZE) * SNAKE_SIZE,
            Math.floor(Math.random() * canvas.height / SNAKE_SIZE) * SNAKE_SIZE
            ];
        }

        const onKeyDown = (event: KeyboardEvent) => {
            switch (event.keyCode) {
                case 65:
                case 37:
                    // LEFT
                    if (dx === 0) {
                        dx = -SNAKE_SIZE;
                        dy = 0;
                    }
                    break;
                case 87:
                case 38:
                    // UP
                    if (dy === 0) {
                        dx = 0;
                        dy = -SNAKE_SIZE;
                    }
                    break;
                case 68:
                case 39:
                    // RIGHT
                    if (dx === 0) {
                        dx = SNAKE_SIZE;
                        dy = 0;
                    }
                    break;
                case 83:
                case 40:
                    // DOWN
                    if (dy === 0) {
                        dx = 0;
                        dy = SNAKE_SIZE;
                    }
                    break;
            }
        };

        requestAnimationFrame(gameTick);

        window.addEventListener('keydown', onKeyDown);
        return () => window.removeEventListener('keydown', onKeyDown);

    }, [canvas]);

    // Round to nearest multiple of 10
    const roundTo5 = (n: number) => Math.round(n / 10) * 10;

    return <canvas
        ref={setCanvas}
        width={roundTo5(props.width) * 4}
        height={roundTo5(props.height) * 4}
        style={{ position: 'absolute', left: 0, top: 0, transformOrigin: '0 0', transform: 'scale(.25)' }}
    />;
};

const InfoText = styled.div<{ clickable?: boolean }>`
    margin: 1px 5px;
    font-size: 8px;
    line-height: 8px;

    ${p => p.clickable && css`
    pointer-events: all;
        cursor: pointer;
        &:hover {
            text-decoration: underline;
        }
    `}
`;

export const JukeboxSnake = () => {
    const [elmt, setElmt] = React.useState<HTMLDivElement | null>(null);
    const [score, setScore] = React.useState(0);
    const [dead, setDead] = React.useState(false);
    const [game, setGame] = React.useState(0);
    const reset = () => {
        setScore(0);
        setDead(false);
        setGame(x => x + 1);
    };
    return <Flexer pointerEvents="none" grow flex="column">
        <Flexer ref={setElmt} grow relative>
            {elmt && <Snake
                key={game}
                width={elmt?.clientWidth}
                height={elmt?.clientHeight}
                onScore={setScore}
                onGameOver={() => {
                    setDead(true);
                    telemetry.emit('interaction.snake', { score });
                }}
            />}
        </Flexer>
        <Flexer flex="row" align="center" justify="space-between" shrink>
            <InfoText>Score: {score}</InfoText>
            {dead && <InfoText clickable onClick={reset}>New Game</InfoText>}
        </Flexer>
    </Flexer>;
};
